// Файл csrf.js

/**
 * Модуль обслуживает сценарий `csrf` в разделе `services`.
 * Он содержит обработчики и данные, относящиеся к этой части клиентского
 * интерфейса.
 * Код связывает локальную логику с соседними компонентами без
 * дублирования общих правил.
 * Такое разделение упрощает сопровождение, проверку поведения и
 * дальнейшее расширение.
 */

import axios from 'axios';
import { process_api_error } from '../utils/session_recovery';



const csrf_refresh_dedupe_ms = 1000;

let csrf_token = '';
let csrf_token_request = null;
let csrf_token_refreshed_at = 0;

const csrf_request_url = (api_base_url) => (
    `${api_base_url}/api/csrf_token_v2`
);


const load_csrf_token = async (api_base_url) => {
    if (csrf_token) {
        return csrf_token;
    }

    if (!csrf_token_request) {
        csrf_token_request = axios.get(
            csrf_request_url(api_base_url),
            {
                withCredentials: true,
                timeout: 10000,
            }
        ).then((response) => {
            csrf_token = String(response.data?.csrf_token || '');

            if (!csrf_token) {
                throw new Error('Сервер не вернул CSRF-токен');
            }

            return csrf_token;
        }).finally(() => {
            csrf_token_request = null;
        });
    }

    return csrf_token_request;
};

const add_csrf_token = async (api_base_url, config) => {
    const method = String(
        config.method || 'get').toLowerCase();

    if (!['post', 'put', 'patch', 'delete'].includes(method)) {
        return config;
    }

    const token = await load_csrf_token(api_base_url);

    if (typeof config.headers?.set === 'function') {
        config.headers.set('X-CSRF-Token', token);

        return config;
    }

    return {
        ...config,
        headers: {
            ...(config.headers || {}),
            'X-CSRF-Token': token,
        },
    };
};

const retry_after_csrf_refresh = async (
    api_base_url,
    error,
) => {
    const original_config = error?.config;
    const error_response = String(
        error?.response?.data?.response || ''
    );

    if (
        error?.response?.status !== 403
        || error_response !== 'CSRF-проверка не пройдена'
        || !original_config
        || original_config._csrf_retry
    ) {
        return Promise.reject(error);
    }

    original_config._csrf_retry = true;
    reset_csrf_token();
    
    const refreshed_token = await load_csrf_token(api_base_url);

    if (typeof original_config.headers?.set === 'function') {
        original_config.headers.set(
            'X-CSRF-Token', refreshed_token);

    } else {
        original_config.headers = {
            ...(original_config.headers || {}),
            'X-CSRF-Token': refreshed_token,
        };
    }

    return axios.request(original_config);
};

const configure_csrf = (client, api_base_url) => {
    client.interceptors.request.use(
        (config) => add_csrf_token(api_base_url, config)
    );
    client.interceptors.response.use(
        (response) => response,
        (error) => process_api_error(
            error,
            (csrf_error) => retry_after_csrf_refresh(
                api_base_url,
                csrf_error,
            ),
        ),
    );
};

const reset_csrf_token = () => {
    csrf_token = '';
    csrf_token_request = null;
    csrf_token_refreshed_at = 0;
};

const refresh_csrf_token = async (api_base_url) => {
    const recently_refreshed = (
        csrf_token
        && Date.now() - csrf_token_refreshed_at
        < csrf_refresh_dedupe_ms
    );

    if (recently_refreshed) {
        return csrf_token;
    }

    reset_csrf_token();
    const refreshed_token = await load_csrf_token(api_base_url);
    csrf_token_refreshed_at = Date.now();

    return refreshed_token;
};


export {
    configure_csrf,
    refresh_csrf_token,
    reset_csrf_token,
};
